import { ImpersonationService } from "@/client"
import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { FiX } from "react-icons/fi"

function getOrgIdFromToken(token: string): string | null {
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return null
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
    )
    return payload.org_id || null
  } catch {
    return null
  }
}

export function ImpersonationBanner() {
  const { data: status } = useQuery({
    queryKey: ["impersonationStatus"],
    queryFn: () => ImpersonationService.getImpersonationStatus(),
  })

  const stopImpersonationMutation = useMutation({
    mutationFn: () => ImpersonationService.stopImpersonation(),
    onSuccess: (data) => {
      // Update the token in localStorage
      localStorage.setItem("access_token", data.access_token)

      // Redirect to the org members page so the admin can select another user.
      // Use the org_id from the restored admin token to land on the correct org.
      // Do NOT invalidate queries before navigating — this avoids race conditions
      // where queries refetch with the old token and receive 403 errors.
      const orgId = getOrgIdFromToken(data.access_token)
      window.location.href = orgId
        ? `/organizations/${orgId}/members`
        : "/organizations"
    },
  })

  if (!status?.is_impersonating || !status.impersonated_user) {
    return null
  }

  const displayName =
    status.impersonated_user.full_name || status.impersonated_user.email

  return (
    <Box
      bg="orange.500"
      color="white"
      px={4}
      py={{ base: 2, md: 3 }}
      position="sticky"
      top={0}
      zIndex={1000}
      boxShadow="md"
    >
      <Flex
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        maxW="container.xl"
        mx="auto"
        gap={2}
      >
        <Box flex={1} minW={0}>
          <HStack gap={1.5} flexWrap="nowrap">
            <Text
              fontWeight="bold"
              fontSize={{ base: "xs", md: "sm" }}
              flexShrink={0}
            >
              Impersonating:
            </Text>
            <Text fontSize={{ base: "xs", md: "sm" }} truncate>
              {displayName}
            </Text>
          </HStack>
          {status.impersonated_user.full_name && (
            <Text opacity={0.85} fontSize="xs" mt={0.5}>
              {status.impersonated_user.email}
            </Text>
          )}
        </Box>

        <Button
          size="xs"
          variant="outline"
          colorPalette="whiteAlpha"
          aria-label="Stop Impersonating"
          onClick={() => stopImpersonationMutation.mutate()}
          loading={stopImpersonationMutation.isPending}
          flexShrink={0}
        >
          <FiX />
          <Text display={{ base: "none", sm: "inline" }}>
            Stop Impersonating
          </Text>
          <Text display={{ base: "inline", sm: "none" }}>Stop</Text>
        </Button>
      </Flex>
    </Box>
  )
}
